import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { isRebuyExcludedLine, selectRebuyProducts } from '@bbd/cart-rules';

/** Cards in "Maak je look compleet": Mix & match first, Rebuy fills the rest */
const MAX_CARDS = 10;

class BbdRebuyCartCrosssells extends HTMLElement {
  /**
   * The cached recommendations
   * @type {Record<string, any>}
   */
  #cachedRecommendations = {};

  /**
   * Bound onCartUpdate function
   * @type {Function | null}
   */
  #onCartUpdateBound = null;

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** Whether a fresh render is waiting to be reported once the accordion opens */
  #pendingDatalayer = false;

  /** Bound toggle handler for the parent <details> element */
  #onDetailsToggleBound = null;

  connectedCallback() {
    this.#loadRecommendations();
    this.#onCartUpdateBound = this.#onCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateBound);

    this.#onDetailsToggleBound = this.#onDetailsToggle.bind(this);
    this.closest('details')?.addEventListener('toggle', this.#onDetailsToggleBound);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateBound);
    this.closest('details')?.removeEventListener('toggle', this.#onDetailsToggleBound);
  }

  /**
   * Trigger a reload when the cart is updated
   */
  #onCartUpdate() {
    this.#loadRecommendations();
  }

  /**
   * Fire the datalayer event when the accordion opens, if a render is pending
   * @param {ToggleEvent} event
   */
  #onDetailsToggle(event) {
    if (!event.target.open || !this.#pendingDatalayer) return;
    this.#fireDatalayerEvent();
  }

  /**
   * Dispatch datalayer:list-ready for the crosssells container
   */
  #fireDatalayerEvent() {
    const container = this.querySelector('[data-datalayer-list-name]');
    if (!container) return;
    this.#pendingDatalayer = false;
    this.dispatchEvent(new CustomEvent('datalayer:list-ready', { bubbles: true, detail: { container } }));
  }

  /**
   * Build query string for cart[items] array
   * @param {any[]} items
   * @returns {string}
   */
  #buildCartItemsQueryString(items) {
    return items
      .map((item, index) => {
        const prefix = `cart[items][${index}]`;
        return `${encodeURIComponent(`${prefix}[product_id]`)}=${item.product_id}&${encodeURIComponent(
          `${prefix}[variant_id]`
        )}=${item.variant_id}&${encodeURIComponent(`${prefix}[quantity]`)}=${item.quantity}`;
      })
      .join('&');
  }

  /**
   * Load the recommendations from Rebuy
   */
  async #loadRecommendations() {
    const { rebuyId, apiKey, customerId } = this.dataset;

    if (!rebuyId || !apiKey) {
      this.#handleError(new Error('Rebuy Widget ID and API Key are required'));
      return;
    }

    // Reset visibility if it was previously hidden
    const accordion = this.closest('accordion-custom.cart-crosssells');
    accordion?.classList.remove('hidden');
    this.classList.remove('hidden');

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      // Fetch the current cart data
      const cartResponse = await fetch('/cart.js', { signal: controller.signal });
      const cart = await cartResponse.json();

      if (!cart.items || cart.items.length === 0) {
        this.#handleError(new Error('Cart is empty'));
        return;
      }

      // Giftboxes and free gift lines (Monk lines carry _mr) are not sent to Rebuy
      const rebuyItems = cart.items.filter((item) => !this.#isGiftOrGiftbox(item));

      if (rebuyItems.length === 0) {
        this.#handleError(new Error('No cart lines to base recommendations on'));
        return;
      }

      // Try each product in the cart until we get recommendations
      for (const item of rebuyItems) {
        const productId = item.product_id;
        const variantId = item.variant_id;

        const params = new URLSearchParams({
          key: apiKey,
          shopify_product_ids: productId,
          shopify_variant_ids: variantId,
          cart_subtotal: rebuyItems.reduce((total, line) => total + line.final_line_price, 0),
          cart_line_count: rebuyItems.length,
          cart_item_count: rebuyItems.reduce((total, line) => total + line.quantity, 0),
          limit: '10'
        });

        if (customerId) {
          params.set('shopify_customer_id', customerId);
        }

        const url = `https://rebuyengine.com/api/v1/custom/id/${rebuyId}?${params.toString()}`;

        // Check cache first
        const cachedResponse = this.#cachedRecommendations[url];
        if (cachedResponse && cachedResponse.data?.length > 0) {
          const cachedProducts = this.#selectProducts(cachedResponse.data, cart);
          if (cachedProducts.length > 0) {
            this.#renderRecommendations(cachedProducts);
            return;
          }
        }

        // Fetch from API
        const response = await fetch(url, { signal: controller.signal });

        if (response.ok) {
          const data = await response.json();
          // If we have products, cache and render. Products that are already in the cart or in the Mix & match
          // cards are skipped, then the next cart line gets a chance.
          if (data.data && data.data.length > 0) {
            this.#cachedRecommendations[url] = data;
            const products = this.#selectProducts(data.data, cart);
            if (products.length > 0) {
              this.#renderRecommendations(products);
              return;
            }
          }
        }

        // If we reach here, this specific product returned no results or error.
        // The loop continues to the next item in the cart.
      }

      // If the loop finishes without returning, no items produced recommendations
      this.#handleError(new Error('No recommendations returned for any product in the cart'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#handleError(error);
    } finally {
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Whether a cart line is a free gift or a giftbox
   * @param {{ product_id: number, properties?: Record<string, string> | null }} item
   * @returns {boolean}
   */
  #isGiftOrGiftbox(item) {
    return isRebuyExcludedLine(item, this.#excludedProductIds);
  }

  /**
   * Product ids of the giftboxes, from the shop metafield
   * @returns {Set<string>}
   */
  get #excludedProductIds() {
    return new Set((this.dataset.excludedProductIds ?? '').split(',').filter(Boolean));
  }

  /**
   * The Rebuy products to show: not in the cart, not a giftbox, not already in the Mix & match cards, and as many as
   * fit in the 10 cards of the section.
   * @param {any[]} products
   * @param {{ items: { product_id: number }[] }} cart
   * @returns {any[]}
   */
  #selectProducts(products, cart) {
    const mixMatchIds = [
      ...(this.closest('.bbd-look')?.querySelectorAll('[data-mix-match-group] [data-datalayer-product-id]') ?? [])
    ].map((card) => String(card.getAttribute('data-datalayer-product-id')));

    return selectRebuyProducts(products, {
      cartProductIds: cart.items.map((item) => item.product_id),
      mixMatchIds,
      giftboxIds: this.#excludedProductIds,
      maxCards: MAX_CARDS
    });
  }

  /**
   * Render the recommendations
   * @param {any[]} products
   */
  async #renderRecommendations(products) {
    if (products.length === 0) {
      this.#handleError(new Error('No recommendations returned from Rebuy'));
      return;
    }

    const productIds = products.map((product) => product.id);
    const searchQuery = productIds.map((id) => `id:${id}`).join(' OR ');

    // We use the Section Renderer API with the predictive search endpoint
    // to fetch the section HTML with the product IDs
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      this.#handleError(new Error('Section ID is required for rendering'));
      return;
    }

    // eslint-disable-next-line
    const url = new URL(Theme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', searchQuery);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(Math.min(productIds.length, 10)));

    try {
      const htmlText = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');
      const newContent = doc.querySelector('.bbd-look__group');

      if (newContent && newContent.innerHTML.trim().length) {
        this.innerHTML = newContent.outerHTML;
        this.dataset.recommendationsPerformed = 'true';
        this.#pendingDatalayer = true;
        if (this.closest('details')?.open) {
          this.#fireDatalayerEvent();
        }
      } else {
        this.#handleError(new Error('Failed to render recommendations HTML'));
      }
    } catch (error) {
      this.#handleError(error);
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('BBD Rebuy Cart Cross-Sells error:', error.message);
    this.dataset.error = 'Error loading Rebuy recommendations';

    // Nothing to show from Rebuy: remove the placeholder. The section stays when Mix & match cards are there,
    // otherwise hide the whole accordion.
    const hasMixMatch = this.closest('.bbd-look')?.querySelector('[data-mix-match-group] [data-datalayer-item]');
    if (hasMixMatch) {
      this.replaceChildren();
      return;
    }

    const accordion = this.closest('accordion-custom.cart-crosssells');
    if (accordion) {
      accordion.classList.add('hidden');
    } else {
      this.classList.add('hidden');
    }
  }
}

if (!customElements.get('bbd-rebuy-cart-crosssells')) {
  customElements.define('bbd-rebuy-cart-crosssells', BbdRebuyCartCrosssells);
}
